import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Save, CheckCircle2, FileSpreadsheet, Download, Trash2, RefreshCw, FileText, AlertCircle, Folder, Sparkles, Loader2, Wrench } from 'lucide-react';
import { SmartUploadZone } from '../components/SmartUploadZone';
import { FixRowsDrawer } from '../components/FixRowsDrawer';

export const Files = () => {
  const { selectedClient, API } = useClient();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentRecords, setRecentRecords] = useState([]);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [fixingFile, setFixingFile] = useState(null);

  const fetchFiles = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/files/${selectedClient.id}`);
      setFiles(res.data.files || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecent = async () => {
    if (!selectedClient) return;
    try {
      const res = await axios.get(`${API}/financial/records/${selectedClient.id}?limit=10`);
      setRecentRecords(res.data.records);
    } catch (e) {}
  };

  useEffect(() => {
    fetchFiles();
    fetchRecent();
  }, [selectedClient]);

  if (!selectedClient) return <div className="text-center text-text-secondary py-12">Select a client</div>;

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/export/excel/${selectedClient.id}?days=90`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedClient.name.replace(/\s+/g, '_')}_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Export failed');
    }
  };

  const totalRecords = files.reduce((s, f) => s + (f.records_count || 0), 0);
  const totalSize = files.reduce((s, f) => s + (f.size_bytes || 0), 0);

  // Module counts
  const moduleCounts = files.reduce((acc, f) => {
    const m = f.module || 'pos';
    acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});
  const filteredFiles = moduleFilter === 'all' ? files : files.filter(f => (f.module || 'pos') === moduleFilter);
  const MODULES = [
    { key: 'all', label: 'All', icon: '📂' },
    { key: 'revenue', label: 'Revenue', icon: '💰' },
    { key: 'expenses', label: 'Expenses', icon: '📉' },
    { key: 'suppliers', label: 'Suppliers', icon: '🧾' },
    { key: 'reconciliation', label: 'Bank Recon', icon: '🏦' },
    { key: 'menu_performance', label: 'POS Sales', icon: '🍽️' },
    { key: 'menu', label: 'Menu', icon: '📋' },
    { key: 'recipes', label: 'Recipes', icon: '📜' },
    { key: 'reviews', label: 'Reviews', icon: '⭐' },
    { key: 'mandi', label: 'Mandi', icon: '🌾' },
    { key: 'pos', label: 'Legacy POS', icon: '📊' },
  ];

  return (
    <div className="space-y-6" data-testid="files-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Files & Uploads</h2>
          <p className="text-text-secondary mt-1">{selectedClient.name} · {files.length} files uploaded</p>
        </div>
        <Button onClick={handleExportExcel} className="bg-state-success hover:opacity-90 text-white" data-testid="export-excel-button">
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Export to Excel
        </Button>
      </div>

      <Tabs defaultValue="files" className="space-y-6">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="files" data-testid="tab-files">
            <Folder className="h-3 w-3 mr-1" /> Uploaded Files
          </TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="recent" data-testid="tab-recent">Recent Records</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryCard label="Total Files" value={files.length} />
            <SummaryCard label="Total Records" value={totalRecords.toLocaleString()} />
            <SummaryCard label="Total Size" value={formatBytes(totalSize)} />
          </div>

          {/* Upload zone — Smart Upload auto-classifies any file format */}
          <SmartUploadZone clientId={selectedClient.id} API={API} onUploadComplete={() => { fetchFiles(); fetchRecent(); }} />

          {/* Module filter pills */}
          <div className="flex flex-wrap gap-2" data-testid="module-filter-bar">
            {MODULES.map(m => {
              const count = m.key === 'all' ? files.length : (moduleCounts[m.key] || 0);
              const active = moduleFilter === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setModuleFilter(m.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors border ${
                    active
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-text-secondary border-border hover:border-primary'
                  }`}
                  data-testid={`module-filter-${m.key}`}
                >
                  <span className="mr-1">{m.icon}</span>{m.label} <span className="opacity-70 ml-1">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Files list */}
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-background-secondary flex justify-between items-center">
              <h3 className="text-lg font-semibold text-primary">
                {moduleFilter === 'all' ? 'All Uploaded Files' : MODULES.find(x => x.key === moduleFilter)?.label + ' Files'}
                <span className="text-text-muted text-sm font-normal ml-2">({filteredFiles.length})</span>
              </h3>
              <div className="flex space-x-2">
                <PurgeFailedButton clientId={selectedClient.id} API={API} files={files} onPurged={fetchFiles} />
                <Button onClick={fetchFiles} variant="outline" size="sm" data-testid="refresh-files">Refresh</Button>
              </div>
            </div>
            {loading ? (
              <div className="p-8 text-center text-text-muted">Loading...</div>
            ) : filteredFiles.length === 0 ? (
              <div className="p-12 text-center">
                <Folder className="h-12 w-12 mx-auto text-text-muted opacity-30 mb-3" />
                <p className="text-text-muted">No files in this category yet</p>
                <p className="text-xs text-text-muted mt-1">Upload from the relevant module (Suppliers, Recipes, etc.) — AI will extract and import automatically</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-primary text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">File Name</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Module</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Rows</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Auto-Imported</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Pending</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Conf.</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Data Quality</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Uploaded</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredFiles.map((f, idx) => (
                      <FileRow key={f.id} file={f} idx={idx} api={API} clientId={selectedClient?.id} onDeleted={fetchFiles} onFix={setFixingFile} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="manual">
          <ManualEntryForm client={selectedClient} API={API} onSaved={fetchRecent} />
        </TabsContent>

        <TabsContent value="recent">
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-border bg-background-secondary">
              <h3 className="text-lg font-semibold text-primary">Last 10 Daily Records</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Revenue</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Card</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Cash</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Food</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Labor</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Covers</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentRecords.length === 0 ? (
                    <tr><td colSpan="8" className="text-center text-text-muted py-8">No records yet</td></tr>
                  ) : recentRecords.map((r, idx) => (
                    <tr key={r.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'}>
                      <td className="px-4 py-3 text-sm font-medium">{r.date}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(r.revenue || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(r.card_payments || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(r.cash_payments || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(r.food_cost || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(r.labor_cost || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right">{r.covers || 0}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(r.tax_amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {fixingFile && (
        <FixRowsDrawer
          file={fixingFile}
          clientId={selectedClient?.id}
          api={API}
          onClose={() => setFixingFile(null)}
          onChanged={fetchFiles}
        />
      )}
    </div>
  );
};

const FileRow = ({ file, idx, api, clientId, onDeleted, onFix }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDownload = () => {
    window.open(`${api}/files/download/${file.id}`, '_blank');
  };

  const handleDelete = async () => {
    // First fetch impact preview
    let impact = null;
    try {
      const res = await axios.get(`${api}/files/${file.id}/impact`);
      impact = res.data;
    } catch (_) { /* preview optional */ }

    const total = impact?.total_imported_records || 0;
    let cascade = false;
    if (total > 0) {
      const lines = Object.entries(impact.imported_records_by_collection || {})
        .filter(([, n]) => n > 0)
        .map(([c, n]) => `   • ${n} row(s) in ${c}`)
        .join('\n');
      const yes = window.confirm(
        `Delete "${file.original_filename}"?\n\n` +
        `This file imported ${total} record(s):\n${lines}\n\n` +
        `OK = delete file AND those imported records (cascade)\n` +
        `Cancel = keep this file`
      );
      if (!yes) return;
      cascade = true;
    } else {
      if (!window.confirm(`Delete "${file.original_filename}"? (This file has no tracked imports.)`)) return;
    }

    setDeleting(true);
    try {
      const res = await axios.delete(`${api}/files/${file.id}?cascade=${cascade}`);
      if (cascade && res.data?.removed) {
        const removedSummary = Object.entries(res.data.removed)
          .filter(([, n]) => n > 0)
          .map(([c, n]) => `${n} ${c}`)
          .join(', ');
        if (removedSummary) {
          // Brief, non-blocking feedback
          console.info(`Cascade removed: ${removedSummary}`);
        }
      }
      onDeleted();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };

  const TYPE_BADGE_MAP = {
    csv: 'bg-blue-100 text-state-info',
    excel: 'bg-green-100 text-state-success',
    pdf: 'bg-red-100 text-state-error',
    image: 'bg-purple-100 text-purple-700',
    text: 'bg-gray-100 text-text-secondary'
  };
  const TYPE_BADGE = TYPE_BADGE_MAP[file.file_type] || 'bg-gray-100 text-text-secondary';

  return (
    <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'} data-testid={`file-row-${file.id}`}>
      <td className="px-4 py-3 text-sm font-medium text-text-primary">
        <div className="flex items-center space-x-2">
          <FileText className="h-4 w-4 text-text-muted flex-shrink-0" />
          <span className="truncate max-w-xs">{file.original_filename}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-background-secondary text-text-secondary">
          {file.module || 'pos'}
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase ${TYPE_BADGE}`}>
          {file.file_type}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-right">{(file.records_count || 0).toLocaleString()}</td>
      <td className="px-4 py-3 text-sm text-right text-state-success font-semibold" title={
        file.transactions_written > 0
          ? `${file.transactions_written} bill-level rows captured in pos_transactions; ${file.auto_imported ?? 0} daily rollup(s) in daily_financial`
          : undefined
      }>
        {file.transactions_written > 0
          ? file.transactions_written.toLocaleString()
          : (file.auto_imported != null ? file.auto_imported : '—')}
      </td>
      <td className="px-4 py-3 text-sm text-right">
        {file.pending_count != null && file.pending_count > 0 ? (
          <button
            onClick={() => onFix && onFix(file)}
            className="text-state-warning hover:text-amber-900 font-semibold underline decoration-dotted"
            title="Click to review & fix pending rows"
            data-testid={`pending-link-${file.id}`}
          >
            {file.pending_count}
          </button>
        ) : (
          <span className="text-state-warning font-semibold">{file.pending_count != null ? file.pending_count : '—'}</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-right text-text-muted">{file.ai_confidence != null ? `${file.ai_confidence}%` : '—'}</td>
      <td className="px-4 py-3 text-center" data-testid={`dq-cell-${file.id}`}>
        <DataQualityCell dq={file.data_quality} />
      </td>
      <td className="px-4 py-3 text-center">
        {file.status === 'processed' ? (
          <span className="inline-flex items-center text-state-success text-xs font-semibold">
            <CheckCircle2 className="h-3 w-3 mr-1" /> Processed
          </span>
        ) : (
          <span className="inline-flex items-center text-state-error text-xs font-semibold" title={file.error}>
            <AlertCircle className="h-3 w-3 mr-1" /> Failed
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-text-muted">{new Date(file.uploaded_at).toLocaleString()}</td>
      <td className="px-4 py-3 text-center">
        <div className="flex justify-center space-x-1">
          {(file.pending_count > 0 || file.status === 'failed') && (
            <button
              onClick={() => onFix && onFix(file)}
              className="text-state-warning hover:bg-amber-50 rounded p-1"
              title="Fix unimported rows"
              data-testid={`fix-rows-btn-${file.id}`}
            >
              <Wrench className="h-4 w-4" />
            </button>
          )}
          {file.module && file.module !== 'pos' && (
            <ReprocessButton fileId={file.id} api={api} module={file.module} />
          )}
          <button onClick={handleDownload} className="text-primary hover:bg-background-secondary rounded p-1" title="Download" data-testid={`download-file-${file.id}`}>
            <Download className="h-4 w-4" />
          </button>
          <button onClick={handleDelete} disabled={deleting} className="text-state-error hover:bg-red-50 rounded p-1" title="Delete" data-testid={`delete-file-${file.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const ReprocessButton = ({ fileId, api, module }) => {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!window.confirm(`Re-run AI extraction on this file? (existing pending rows for this file remain; new rows will be added)`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${api}/files/${fileId}/reprocess`);
      const bills = res.data.transactions_written || 0;
      const summary = bills > 0
        ? `Re-processed: ${bills} bills captured · ${res.data.auto_imported ?? 0} daily rollup${(res.data.auto_imported ?? 0) === 1 ? '' : 's'} · ${res.data.pending_review} need review`
        : `Re-processed: ${res.data.rows_extracted} rows · ${res.data.auto_imported} imported · ${res.data.pending_review} need review`;
      alert(summary);
      window.location.reload();
    } catch (e) {
      alert('Re-process failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <button onClick={handle} disabled={busy} className="text-state-info hover:bg-blue-50 rounded p-1" title="Re-process with AI" data-testid={`reprocess-file-${fileId}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
    </button>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="bg-white border border-border rounded-md p-4">
    <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">{label}</p>
    <p className="text-2xl font-bold text-primary">{value}</p>
  </div>
);

const DataQualityCell = ({ dq }) => {
  if (!dq || (dq.arithmetic === 0 && dq.imputed === 0 && dq.ai_repaired === 0 && (dq.remaining_suspicious || 0) === 0)) {
    return <span className="text-text-muted text-xs">—</span>;
  }
  const fixed = (dq.arithmetic || 0) + (dq.imputed || 0) + (dq.ai_repaired || 0);
  const left = dq.remaining_suspicious || 0;
  const tooltip = `Arithmetic: ${dq.arithmetic || 0} · Imputed: ${dq.imputed || 0} · AI: ${dq.ai_repaired || 0} · Unresolved: ${left}`;
  return (
    <div className="inline-flex items-center space-x-1 text-[11px]" title={tooltip}>
      {fixed > 0 && (
        <span className="bg-blue-100 text-state-info px-1.5 py-0.5 rounded font-semibold" data-testid="dq-fixed">
          ✨ {fixed} fixed
        </span>
      )}
      {left > 0 && (
        <span className="bg-yellow-100 text-state-warning px-1.5 py-0.5 rounded font-semibold" data-testid="dq-unresolved">
          ⚠ {left} left
        </span>
      )}
    </div>
  );
};


const PurgeFailedButton = ({ clientId, API, files, onPurged }) => {
  const failedCount = files.filter(f => f.status === 'failed').length;
  const [busy, setBusy] = useState(false);
  if (failedCount === 0) return null;
  const handlePurge = async () => {
    if (!window.confirm(`Permanently delete ${failedCount} failed upload(s) (DB rows + disk files)?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/files/purge-failed/${clientId}`);
      onPurged();
      alert(`Removed ${res.data.removed} failed upload(s).`);
    } catch (e) {
      alert('Purge failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <Button onClick={handlePurge} disabled={busy} variant="outline" size="sm"
      className="border-state-error text-state-error hover:bg-red-50"
      data-testid="purge-failed-button">
      <Trash2 className="h-3.5 w-3.5 mr-1" />
      Purge {failedCount} Failed
    </Button>
  );
};

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const ManualEntryForm = ({ client, API, onSaved }) => {
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    revenue: '', card_payments: '', cash_payments: '',
    food_cost: '', beverage_cost: '', labor_cost: '',
    other_expenses: '', covers: '', tax_amount: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/financial/records`, {
        client_id: client.id,
        date: form.date,
        revenue: parseFloat(form.revenue) || 0,
        card_payments: parseFloat(form.card_payments) || 0,
        cash_payments: parseFloat(form.cash_payments) || 0,
        food_cost: parseFloat(form.food_cost) || 0,
        beverage_cost: parseFloat(form.beverage_cost) || 0,
        labor_cost: parseFloat(form.labor_cost) || 0,
        other_expenses: parseFloat(form.other_expenses) || 0,
        covers: parseInt(form.covers) || 0,
        tax_amount: parseFloat(form.tax_amount) || 0,
        category_breakdown: {}
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      setForm({...form, revenue: '', card_payments: '', cash_payments: '', food_cost: '', beverage_cost: '', labor_cost: '', other_expenses: '', covers: '', tax_amount: ''});
      onSaved();
    } catch (err) {
      alert('Error saving record');
    } finally {
      setSubmitting(false);
    }
  };

  const Field = ({ label, value, onChange, required, testid, type = "number" }) => (
    <div>
      <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">{label}{required && ' *'}</label>
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
        required={required}
        data-testid={testid}
      />
    </div>
  );

  return (
    <div className="bg-white border border-border rounded-md p-6 max-w-4xl">
      <h3 className="text-lg font-semibold text-primary mb-2">Daily Financial Entry</h3>
      <p className="text-sm text-text-secondary mb-6">Enter daily numbers manually. If a date already exists, this will update it.</p>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">Date *</label>
          <input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})}
            className="w-full md:w-1/3 border border-border rounded-md px-3 py-2" required data-testid="entry-date" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Total Revenue (₹)" value={form.revenue} onChange={(v) => setForm({...form, revenue: v})} required testid="entry-revenue" />
          <Field label="Card Payments (₹)" value={form.card_payments} onChange={(v) => setForm({...form, card_payments: v})} testid="entry-card" />
          <Field label="Cash Payments (₹)" value={form.cash_payments} onChange={(v) => setForm({...form, cash_payments: v})} testid="entry-cash" />
        </div>
        <div className="border-t border-border pt-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-3">Cost Breakdown</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Food Cost (₹)" value={form.food_cost} onChange={(v) => setForm({...form, food_cost: v})} testid="entry-food-cost" />
            <Field label="Beverage Cost (₹)" value={form.beverage_cost} onChange={(v) => setForm({...form, beverage_cost: v})} testid="entry-bev-cost" />
            <Field label="Labor Cost (₹)" value={form.labor_cost} onChange={(v) => setForm({...form, labor_cost: v})} testid="entry-labor" />
            <Field label="Other Expenses (₹)" value={form.other_expenses} onChange={(v) => setForm({...form, other_expenses: v})} testid="entry-other" />
          </div>
        </div>
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Covers (Guest Count)" value={form.covers} onChange={(v) => setForm({...form, covers: v})} testid="entry-covers" />
            <Field label="Tax / GST Collected (₹)" value={form.tax_amount} onChange={(v) => setForm({...form, tax_amount: v})} testid="entry-tax" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary-hover" data-testid="save-record-button">
            <Save className="mr-2 h-4 w-4" />
            {submitting ? 'Saving...' : 'Save Record'}
          </Button>
          {saved && (
            <span className="text-state-success flex items-center text-sm font-medium" data-testid="save-success">
              <CheckCircle2 className="mr-1 h-4 w-4" /> Saved successfully
            </span>
          )}
        </div>
      </form>
    </div>
  );
};
